import React from "react";
import CVData from "../../types/CVData";
import { Document, Page, View, Text, Image, Font } from "@react-pdf/renderer";
import { styles } from "./components/shared/cvDocStyles";
import { formatDate, formatProjectPositionHeading, sortProjectsByDate } from "../utils/helpers";

interface CVDocumentProps {
  formData: CVData;
  includeName: boolean;
}
Font.register({
  family: "Calibri",
  fonts: [
    { src: "calibri.ttf" },
    { src: "calibri-bold.ttf", fontWeight: "bold" },
    { src: "calibri-italic.ttf", fontStyle: "italic" },
  ],
});

/**
 * Formats a string input to have a different style on the first letters of each word.
 * @param input The string to be formatted.
 * @param firstLetter The style for the first letter(s).
 * @param restOfText The style for the rest of the text.
 * @returns
 */
const formatField = (input: string, firstLetter: any, restOfText: any) => {
  return input.split(" ").map((word, index) => {
    // Handle hyphenated inputs
    const subWords = word.split("-");

    return (
      <Text key={index}>
        {subWords.map((subWord, subIndex) => (
          <Text key={subIndex}>
            <Text style={firstLetter}>{subWord.charAt(0)}</Text>
            <Text style={restOfText}>{subWord.slice(1)}</Text>
            {subIndex < subWords.length - 1 && (
              <Text style={restOfText}>-</Text>
            )}
          </Text>
        ))}
        <Text style={restOfText}> </Text>
      </Text>
    );
  });
};

export const CVDocument: React.FC<CVDocumentProps> = ({
  formData,
  includeName,
}) => {
  const hasFrontendSkills = !!formData.skills.frontend.length;
  const hasBackendSkills = !!formData.skills.backend.length;
  const hasCloudSkills = !!formData.skills.cloud.length;
  const hasDatabaseSkills = (formData.skills.database) ? !!formData?.skills?.database?.length : false;
  const hasToolsSkills = !!formData.skills.tools.length;
  const hasDevOpsSkills = !!formData.skills.devOps.length;
  const hasOtherSkills = !!formData.skills.others.length;

  const hasCertifications = !!formData.certifications?.some((c) =>
    [c?.course, c?.courseId, c?.place, c?.date].some(
      (v) => (v ?? "").trim() !== ""
    )
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Company Logo in Header */}
        <Image src={"synergi-logo.png"} style={styles.header} />
        {!includeName && (
          <Text
            style={{
              position: "absolute",
              top: 30,
              left: 40,
              fontSize: 10,
              color: "#d0d0d0",
              fontStyle: "italic",
            }}
          >
            {formData.cvUId}
          </Text>
        )}
        {/* Name & Position */}
        <View style={[styles.section, { marginBottom: 17 }]}>
          <Text>
            {includeName
              ? formatField(formData.name, styles.nameFirstLetter, styles.name)
              : ""}
          </Text>
          <Text>
            {formatField(
              formData.position.name,
              styles.positionFirstLetter,
              styles.position
            )}
          </Text>
        </View>

        {/* Professional Summary */}
        <View style={styles.section}>
          <Text>
            {formatField(
              "PROFESSIONAL SUMMARY",
              styles.titleFirstLetter,
              styles.title
            )}
          </Text>
          <View style={styles.line} />
          <Text style={styles.text}>{formData.professionalSummary}</Text>
        </View>

        {/* Skill Summary */}
        <View style={styles.section}>
          <Text>
            {formatField(
              "SKILL SUMMARY",
              styles.titleFirstLetter,
              styles.title
            )}
          </Text>
          <View style={styles.line} />
          {hasBackendSkills && (
            <Text style={[styles.text, styles.projectDetailRow]}>
              <Text style={styles.projectDetailLabel}>Backend: </Text>
              {formData.skills.backend
                .map((s) => (typeof s === "string" ? s : s.name))
                .join(", ")}
            </Text>
          )}
          {hasFrontendSkills && (
            <Text style={[styles.text, styles.projectDetailRow]}>
              <Text style={styles.projectDetailLabel}>Frontend: </Text>
              {formData.skills.frontend
                .map((s) => (typeof s === "string" ? s : s.name))
                .join(", ")}
            </Text>
          )}
          {hasDevOpsSkills && (
            <Text style={[styles.text, styles.projectDetailRow]}>
              <Text style={styles.projectDetailLabel}>DevOps: </Text>
              {formData.skills.devOps
                .map((s) => (typeof s === "string" ? s : s.name))
                .join(", ")}
            </Text>
          )}
          {hasCloudSkills && (
            <Text style={[styles.text, styles.projectDetailRow]}>
              <Text style={styles.projectDetailLabel}>Cloud: </Text>
              {formData.skills.cloud
                .map((s) => (typeof s === "string" ? s : s.name))
                .join(", ")}
            </Text>
          )}
          {hasDatabaseSkills && (
            <Text style={[styles.text, styles.projectDetailRow]}>
              <Text style={styles.projectDetailLabel}>Database: </Text>
              {formData.skills.database
                .map((s) => (typeof s === "string" ? s : s.name))
                .join(",")}
            </Text>
          )}
          {hasToolsSkills && (
            <Text style={[styles.text, styles.projectDetailRow]}>
              <Text style={styles.projectDetailLabel}>Tools: </Text>
              {formData.skills.tools
                .map((s) => (typeof s === "string" ? s : s.name))
                .join(", ")}
            </Text>
          )}
          {hasOtherSkills && (
            <Text style={[styles.text, styles.projectDetailRow]}>
              <Text style={styles.projectDetailLabel}>Others: </Text>
              {formData.skills.others
                .map((s) => (typeof s === "string" ? s : s.name))
                .join(",")}
            </Text>
          )}
        </View>

        {/* Professional Affiliations */}
        <View style={styles.section}>
          <Text>
            {formatField(
              "PROFESSIONAL AFFILIATIONS",
              styles.titleFirstLetter,
              styles.title
            )}
          </Text>
          <View style={styles.line} />

          {/* Map employers  */}
          {formData.professionalAffiliations.map((affiliation, index) => (
            <View key={index} style={{ paddingBottom: 20 }}>
              {/* Map projects */}
              {sortProjectsByDate(affiliation.projects).map((project, projIndex) => {
                const isInternship = project.position.includes("Internship");

                return (
                  <View key={projIndex}>
                    {/* Employer name only on the first project */}
                    {projIndex === 0 && (
                      <Text style={styles.text}>
                        <Text style={{ fontWeight: "bold" }}>
                          {affiliation.employer}, {affiliation.location},{" "}
                          {affiliation.country},{" "}
                        </Text>
                        {formatDate(affiliation.clientPeriod)}
                      </Text>
                    )}
                    <View
                      key={projIndex}
                      style={{ marginTop: 10, marginBottom: 6 }}
                    >
                      <Text style={[styles.text, { color: "#2e74b5" }]}>
                        {formatProjectPositionHeading(project.position)}
                      </Text>

                      {!isInternship && (
                        <>
                          <Text style={[styles.text, styles.projectDetailRow]}>
                            <Text style={styles.projectDetailLabel}>
                              Client:{" "}
                            </Text>
                            {project.client}
                          </Text>
                          <Text style={[styles.text, styles.projectDetailRow]}>
                            <Text style={styles.projectDetailLabel}>
                              Role:{" "}
                            </Text>
                            {project.role}
                          </Text>
                          <Text style={[styles.text, styles.projectDetailRow]}>
                            <Text style={styles.projectDetailLabel}>
                              Period:{" "}
                            </Text>
                            {formatDate(project.projectPeriod)}
                          </Text>
                          <Text style={[styles.text, styles.projectDetailRow]}>
                            <Text style={styles.projectDetailLabel}>
                              Project Description:{" "}
                            </Text>
                            {project.projectDescription}
                          </Text>
                        </>
                      )}
                      <Text
                        style={[styles.text, styles.responsibilitiesLabelRow]}
                      >
                        <Text style={styles.projectDetailLabel}>
                          Responsibilities:{" "}
                        </Text>
                      </Text>
                      {project.responsibilities.map(
                        (responsibility, respIndex) => (
                          <Text
                            key={respIndex}
                            style={[styles.text, styles.bulletList]}
                          >
                            {`\u2022`} {responsibility}
                          </Text>
                        )
                      )}
                      <Text style={[styles.text, { marginTop: 6 }]}>
                        <Text style={{ fontWeight: "bold" }}>
                          Key Technologies:{" "}
                        </Text>
                        {project.keyTechnologies.join(", ")}.
                      </Text>
                    </View>
                  </View>
                );
              })}
            </View>
          ))}
        </View>

        {/* Professional Training/Certifications */}
        {hasCertifications && (
          <View style={styles.section} wrap={false}>
            <Text>
              {formatField(
                "PROFESSIONAL TRAINING/CERTIFICATIONS",
                styles.titleFirstLetter,
                styles.title
              )}
            </Text>
            <View style={styles.line} />
            <View style={{ gap: 13 }}>
              {formData.certifications?.map((certification, index) => (
                <View key={index} style={styles.text13}>
                  <Text>
                    <Text style={{ fontWeight: "bold" }}>Course: </Text>
                    {certification.course}
                  </Text>
                  {certification.place && certification.date && (
                    <Text style={styles.text13}>
                      {certification.place},
                      {formatDate(certification.date, false, true)}
                    </Text>
                  )}
                </View>
              ))}
            </View>
          </View>
        )}
        {/* Education */}
        <View style={styles.section} wrap={false}>
          <Text>
            {formatField("EDUCATION", styles.titleFirstLetter, styles.title)}
          </Text>
          <View style={styles.line} />
          <View style={{ gap: 13 }}>
            {formData.education?.map((education, index) => (
              <View
                key={index}
                style={{
                  width: "100%",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                {/* Wrap degree & institution in a View so they stay together */}
                <View style={{ flexDirection: "row", flexShrink: 1 }}>
                  <Text style={styles.edDegree}>{education.degree} </Text>
                  <Text
                    style={[styles.text13, { width: "250", paddingLeft: 5 }]}
                  >
                    {education.institution}
                  </Text>
                </View>

                {/* Separate Text for Period, aligned to the right */}
                <Text style={[styles.text13, { textAlign: "right" }]}>
                  {formatDate(education.period, true)}
                </Text>
              </View>
            ))}
          </View>
        </View>
        {/* Languages */}
        <View style={styles.section} wrap={false}>
          <Text>
            {formatField("LANGUAGES", styles.titleFirstLetter, styles.title)}
          </Text>
          <View style={styles.line} />
          <View style={{ gap: 4 }}>
            <Text style={styles.text13}>
              <Text style={{ fontWeight: "bold" }}>
                {formData.languages.nativeLanguage}:{" "}
              </Text>
              Native
            </Text>
            <Text style={styles.text13}>
              <Text style={{ fontWeight: "bold" }}>English: </Text>
              {formData.languages.english}
            </Text>
            {formData.languages.otherLanguages.map((language, index) => (
              <Text key={index} style={styles.text13}>
                <Text style={{ fontWeight: "bold" }}>{language.name}: </Text>
                {language.level}
              </Text>
            ))}
          </View>
        </View>
      </Page>
    </Document>
  );
};
